import { useEffect, useState } from 'react';
import ScheduledItemModal from '../../../pages/ai-team/hub/ScheduledItemModal';
import {
  mapToCalendarItems,
  storeItemToCalendarItem,
} from '../../../pages/ai-team/hub/useCalendarContent';
import type { CalendarItem, PlatformKey } from '../../../pages/ai-team/hub/calendarContent';
import {
  listContentDrafts,
  updateContentDraft,
  type ContentDraft,
} from '../../../lib/services/agents/contentDraftApi';
import {
  discardCalendarItem,
  fetchCalendarItem,
  openRedoTicketForDraft,
  rescheduleCalendarItem,
} from '../../../lib/services/agents/calendarScheduleApi';
import { discardContentDraft } from '../../../lib/services/agents/contentDraftApi';

/**
 * The panel's Open post: the Marketing Hub's own post editor, opened over the panel (PRD V0.9⑤).
 *
 * <p><b>Why the Hub's editor and not a card.</b> Every fix a B-单条 card asks for — pick a new time,
 * shorten the caption, make the picture again, publish it now, throw it away — already exists in
 * {@link ScheduledItemModal}. The PRD's 「不在卡上做输入框」 is the same decision read from the other
 * side: a card that grew a date picker would be a second, worse copy of this editor that drifts from
 * it. So the card carries no inputs and the panel borrows the real thing.
 *
 * <p><b>Why this wrapper exists at all.</b> The editor wants a {@code CalendarItem}; the card knows a
 * draft id. That gap is all this file is: load the merchant's drafts, build the same items the
 * calendar builds from them, and pick the one this card is about. Everything the merchant then does
 * goes to the same endpoints the Calendar tab calls — no second implementation of reschedule or
 * discard, so the two cannot drift.
 *
 * <p><b>The two exits are the card's two exits.</b> 「Done」 reports back through
 * {@code confirmActionId} and 「Discard Task」 through {@code discardActionId} — the modal closes in
 * the browser and nothing else would tell the server which way it went. Both are also belt-and-braces
 * now: the card follows its task's state, so a post that goes out fine clears its card whether or not
 * this reported anything. Reporting is what makes it immediate instead of eventual.
 */
export default function PanelPostEditor({
  userId,
  draftId,
  onDone,
  onDiscarded,
  onCancel,
}: {
  userId: string;
  /** The piece this card is about. Null while the card carries no draft — then we do not open. */
  draftId: string;
  /** 「Done」: the merchant says they have dealt with it. */
  onDone: () => void;
  /** 「Discard Task」: the piece is abandoned. */
  onDiscarded: () => void;
  /** Closed without either exit (the ✕, or the piece could not be found). */
  onCancel: () => void;
}) {
  const [item, setItem] = useState<CalendarItem | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The merchant's drafts, mapped exactly as the calendar maps them. No window: the piece
        // behind a card can be weeks old — a scheduled post whose time passed sits at the far end
        // of the list — and this branch's endpoint has no limit to narrow it with anyway.
        // A `cal_` id is a scheduled row in yepairag's calendar store, and it is
        // NOT in the draft list — a card about a scheduled piece names the row, a card
        // about a piece written in chat names the draft, and the panel gets both. Asked
        // only the drafts, the editor decided a perfectly live scheduled post was gone
        // and closed itself: the merchant clicked 「Open post」 and the screen did not
        // move (dev 2026-09-18, card 518 → cal_ce8b0098f462).
        //
        // Read by id, not by month. The card carries no date — a cause is not a date —
        // and a month is 2 MB / 9–37 s because the pool carries every version's full
        // body, so guessing three months around today came back as a gateway 502.
        if (draftId.startsWith('cal_')) {
          const row = await fetchCalendarItem(userId, draftId);
          if (cancelled) return;
          const one = row ? storeItemToCalendarItem(row) : null;
          setItem(one);
          setState(one ? 'ready' : 'missing');
          return;
        }

        const drafts: ContentDraft[] = await listContentDrafts(userId);
        const items = mapToCalendarItems(drafts, []);
        // 三种键都认。卡上那个 id 的形态取决于上报它的那一方：yepairag 的工具手里是
        // agent 形态的 draft_<hex>（= 版本上的 draftId），chatbot-api 自己记的是库里的
        // 行 UUID（= 条目 id 去掉 draft- 前缀）。只认一种，另一种就永远找不到那一篇。
        const found =
          items.find(i => i.versions.some(v => v.draftId === draftId)) ??
          items.find(i => i.id === `draft-${draftId}`) ??
          items.find(i => i.id === draftId) ??
          null;
        if (cancelled) return;
        setItem(found);
        setState(found ? 'ready' : 'missing');
      } catch {
        if (!cancelled) setState('missing');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, draftId]);

  // Nothing to show: the draft is gone (already published, already discarded, out of the window).
  // Hand it back rather than holding the merchant in an empty modal — the card's own Delete is
  // still there, and the card will clear itself if the piece did go out.
  useEffect(() => {
    if (state === 'missing') onCancel();
  }, [state, onCancel]);

  if (state !== 'ready' || !item) {
    return null;
  }

  const versionDraftId = (platform: PlatformKey) =>
    item.versions.find(v => v.platform === platform)?.draftId ?? draftId;

  return (
    <ScheduledItemModal
      item={item}
      onClose={onDone}
      onDiscard={async () => {
        try {
          // Same split the Calendar tab makes: a `cal_` row is a scheduled item in yepairag's
          // store, a `draft-` row is a plain chatbot-api draft. Sending either to the other's
          // endpoint deletes nothing and reports success.
          if (item.id.startsWith('cal_')) {
            await discardCalendarItem(userId, item.id);
          } else if (item.id.startsWith('draft-')) {
            await discardContentDraft(userId, item.id.slice('draft-'.length));
          }
        } catch {
          /* Reported anyway: the card's job is to stop asking, and the piece is abandoned either
             way. A discard that failed server-side leaves the task, and the task keeps the card. */
        }
        onDiscarded();
      }}
      onReschedule={async (date, time) => {
        try {
          await rescheduleCalendarItem(userId, item.id, date, time);
          setItem(prev => (prev ? { ...prev, date, time } : prev));
        } catch {
          /* The editor keeps the old time on screen — better than claiming a move that did not
             happen. The merchant can try again or leave it; the card stays either way. */
        }
      }}
      onSaveVersion={async (platform, title, body) => {
        const id = versionDraftId(platform);
        try {
          await updateContentDraft(userId, id, { title, body });
          setItem(prev =>
            prev
              ? {
                  ...prev,
                  versions: prev.versions.map(v =>
                    v.platform === platform ? { ...v, title, body } : v
                  ),
                }
              : prev
          );
        } catch {
          /* Same: leave what the merchant typed on screen rather than silently reverting it. */
        }
      }}
      onRegenerateInChat={async (note, platform) => {
        // Redoing a piece is a chat turn with a ticket, exactly as the Calendar tab does it —
        // the ticket is what ties the new version to this row and this channel.
        try {
          await openRedoTicketForDraft(userId, versionDraftId(platform), { note: note || null });
        } catch {
          /* no-op: the editor stays open and the merchant can try again */
        }
      }}
    />
  );
}
